import { Component } from "@theme/component";

/**
 * A custom element that manages a full-page overlay header menu.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} menuTrigger - The "Menu" / "Chiudi" trigger button.
 * @property {HTMLSpanElement} menuTriggerText - The text span inside the trigger button.
 * @property {HTMLElement} overlay - The full-page overlay element.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
	requiredRefs = ["menuTrigger", "menuTriggerText", "overlay"];

	#abortController = new AbortController();

	connectedCallback() {
		super.connectedCallback();

		const signal = this.#abortController.signal;

		document.addEventListener(
			"keydown",
			(e) => {
				if (e.key === "Escape" && !this.refs.overlay.hidden) {
					this.closeOverlay();
				}
			},
			{ signal },
		);

		this.#setupSubmenuInteractions(signal);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}

	/**
	 * Set up click interactions for submenu items inside the overlay.
	 * @param {AbortSignal} signal
	 */
	#setupSubmenuInteractions(signal) {
		const overlay = this.refs.overlay;
		if (!overlay) return;

		const parentItems = overlay.querySelectorAll(".header-menu__overlay-item");

		parentItems.forEach((item) => {
			const link = item.querySelector("[data-has-children]");
			const submenu = /** @type {HTMLElement | null} */ (
				item.querySelector(".header-menu__overlay-submenu")
			);

			if (!link || !submenu) return;

			link.addEventListener(
				"click",
				(e) => {
					e.preventDefault();
					const isOpen = !submenu.hidden;
					this.#closeAllSubmenus();
					if (!isOpen) {
						submenu.hidden = false;
						link.setAttribute("aria-expanded", "true");
					}
				},
				{ signal },
			);
		});
	}

	/**
	 * Close all open submenus in the overlay.
	 */
	#closeAllSubmenus() {
		const overlay = this.refs.overlay;
		if (!overlay) return;

		overlay.querySelectorAll(".header-menu__overlay-submenu").forEach((sub) => {
			if (sub instanceof HTMLElement) sub.hidden = true;
		});
		overlay.querySelectorAll("[data-has-children]").forEach((link) => {
			link.setAttribute("aria-expanded", "false");
		});
	}

	/**
	 * Toggle the overlay open/closed.
	 */
	toggleOverlay() {
		const overlay = this.refs.overlay;
		if (!overlay) return;

		overlay.hidden ? this.#openOverlay() : this.closeOverlay();
	}

	/**
	 * Open the full-page overlay.
	 */
	#openOverlay() {
		const overlay = this.refs.overlay;
		if (!overlay) return;

		overlay.hidden = false;
		document.body.style.overflow = "hidden";
		this.refs.menuTrigger.setAttribute("aria-expanded", "true");
		this.refs.menuTriggerText.textContent = "Chiudi";

		requestAnimationFrame(() => {
			overlay.classList.add("header-menu__overlay--open");
		});
	}

	/**
	 * Close the full-page overlay.
	 */
	closeOverlay() {
		const overlay = this.refs.overlay;
		if (!overlay) return;

		overlay.classList.remove("header-menu__overlay--open");
		this.refs.menuTrigger.setAttribute("aria-expanded", "false");
		this.refs.menuTriggerText.textContent = "Menu";
		document.body.style.overflow = "";
		this.#closeAllSubmenus();

		const onTransitionDone = () => {
			overlay.hidden = true;
			overlay.removeEventListener("transitionend", onTransitionDone);
		};
		overlay.addEventListener("transitionend", onTransitionDone);

		setTimeout(() => {
			overlay.hidden = true;
			overlay.removeEventListener("transitionend", onTransitionDone);
		}, 400);
	}
}

if (!customElements.get("header-menu")) {
	customElements.define("header-menu", HeaderMenu);
}
